import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Heart, Edit, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PatientInfo = ({ patient, onUpdate }) => {
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({
    name: patient.name || '',
    date_of_birth: patient.date_of_birth || '',
    gender: patient.gender || '',
    health_card_number: patient.health_card_number || '',
    ailment: patient.ailment || '',
    cancer_type: patient.cancer_type || '',
    doctors: (patient.doctors || []).join(', '),
  });

  const handleUpdate = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      const doctorsList = editForm.doctors ? editForm.doctors.split(',').map(d => d.trim()).filter(d => d) : [];
      
      // Calculate age from date of birth
      let age = null;
      if (editForm.date_of_birth) {
        const birthDate = new Date(editForm.date_of_birth);
        const today = new Date();
        age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
      }
      
      await axios.put(
        `${API}/patients/${patient.id}`,
        {
          name: editForm.name,
          date_of_birth: editForm.date_of_birth,
          age: age,
          gender: editForm.gender,
          health_card_number: editForm.health_card_number,
          ailment: editForm.ailment,
          cancer_type: editForm.cancer_type,
          doctors: doctorsList,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Patient information updated');
      setShowEdit(false);
      onUpdate();
    } catch (error) {
      toast.error('Failed to update patient information');
    }
  };

  return (
    <div className="card" data-testid="patient-info" style={{ backgroundColor: '#f0f9f8', border: '2px solid #A2DAD3' }}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold flex items-center gap-2" style={{ color: '#A2DAD3' }}>
          <User size={20} />
          Patient Information
        </h2>
        <Dialog open={showEdit} onOpenChange={setShowEdit}>
          <DialogTrigger asChild>
            <button
              data-testid="edit-patient-info-button"
              className="btn btn-primary text-sm px-4 py-2 flex items-center gap-2"
            >
              <Edit size={14} />
              Edit
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Patient Information</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4" data-testid="edit-patient-form">
              <div>
                <label className="block text-sm font-medium mb-2">Name</label>
                <input
                  data-testid="edit-name-input"
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Patient's name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Date of Birth</label>
                <input
                  data-testid="edit-dob-input"
                  type="date"
                  value={editForm.date_of_birth}
                  onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })}
                  placeholder="Date of Birth"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Gender</label>
                <select
                  data-testid="edit-gender-input"
                  value={editForm.gender}
                  onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Health Card / Insurance Number</label>
                <input
                  data-testid="edit-health-card-input"
                  type="text"
                  value={editForm.health_card_number}
                  onChange={(e) => setEditForm({ ...editForm, health_card_number: e.target.value })}
                  placeholder="e.g., 1234-567-890"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Ailment/Condition</label>
                <input
                  data-testid="edit-ailment-input"
                  type="text"
                  value={editForm.ailment}
                  onChange={(e) => setEditForm({ ...editForm, ailment: e.target.value })}
                  placeholder="e.g., Cancer, Lymphoma"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Cancer Type (if applicable)</label>
                <input
                  data-testid="edit-cancer-type-input"
                  type="text"
                  value={editForm.cancer_type}
                  onChange={(e) => setEditForm({ ...editForm, cancer_type: e.target.value })}
                  placeholder="e.g., Non-Hodgkin Lymphoma, Stage II"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Doctors (comma-separated)</label>
                <input
                  data-testid="edit-doctors-input"
                  type="text"
                  value={editForm.doctors}
                  onChange={(e) => setEditForm({ ...editForm, doctors: e.target.value })}
                  placeholder="e.g., Dr. Smith, Dr. Johnson"
                />
              </div>
              <button data-testid="submit-edit-patient-button" type="submit" className="btn btn-primary w-full">
                Update Information
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        <div>
          <p className="text-sm text-gray-600">Name</p>
          <p className="font-medium text-lg">{patient.name}</p>
        </div>
        
        {patient.date_of_birth && (
          <div>
            <p className="text-sm text-gray-600">Date of Birth</p>
            <p className="font-medium">{new Date(patient.date_of_birth).toLocaleDateString()}</p>
          </div>
        )}
        
        {patient.age !== null && patient.age !== undefined && (
          <div>
            <p className="text-sm text-gray-600">Age</p>
            <p className="font-medium">{patient.age} years</p>
          </div>
        )}
        
        {patient.gender && (
          <div>
            <p className="text-sm text-gray-600">Gender</p>
            <p className="font-medium">{patient.gender}</p>
          </div>
        )}
        
        {patient.health_card_number && (
          <div>
            <p className="text-sm text-gray-600">Health Card / Insurance Number</p>
            <p className="font-medium font-mono">{patient.health_card_number}</p>
          </div>
        )}
        
        {patient.ailment && (
          <div>
            <p className="text-sm text-gray-600">Condition</p>
            <p className="font-medium">{patient.ailment}</p>
          </div>
        )}
        
        {patient.cancer_type && (
          <div>
            <p className="text-sm text-gray-600">Cancer Type</p>
            <p className="font-medium">{patient.cancer_type}</p>
          </div>
        )}
        
        {patient.doctors && patient.doctors.length > 0 && (
          <div>
            <p className="text-sm text-gray-600">Doctors</p>
            <div className="flex flex-wrap gap-2 mt-1">
              {patient.doctors.map((doctor, index) => (
                <span
                  key={index}
                  className="px-3 py-1 rounded-full text-sm"
                  style={{ backgroundColor: '#A2DAD3', color: 'white' }}
                >
                  {doctor}
                </span>
              ))}
            </div>
          </div>
        )}

        {!patient.date_of_birth && !patient.gender && !patient.ailment && !patient.cancer_type && (!patient.doctors || patient.doctors.length === 0) && (
          <p className="text-sm text-gray-500 italic">No medical information added yet. Click Edit to add details.</p>
        )}
      </div>
    </div>
  );
};

export default PatientInfo;
